
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Pill,
  Package,
  Hash,
  ImagePlus,
  ShoppingCart,
  CheckCircle2,
  LoaderCircle,
  X,
} from "lucide-react";

import { usePrescriptionCart } from "../../context/pharmacy/PrescriptionCartContext";
import { fileToBase64 } from "../../utils/fileUtils";
import {
  getMedicineTypes,
  getMedicineUnits,
} from "../../services/medicineService"



import "./OTCPrescriptionForm.css";

export default function OTCPrescriptionForm() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const { addItem } = usePrescriptionCart();

  const [types, setTypes] = useState([]);
  const [units, setUnits] = useState([]);

  const [drugType, setDrugType] = useState("");
  const [drugName, setDrugName] = useState("");
  const [drugDose, setDrugDose] = useState("");
  const [qty, setQty] = useState(1);
  const [unit, setUnit] = useState("");
  const [image, setImage] = useState(null);
  const [sameDrug, setSameDrug] = useState(false);

  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState("");

  // Load medicine types once.
  useEffect(() => {
    let cancelled = false;

    async function loadTypes() {
      try {
        setLoadingTypes(true);
        const data = await getMedicineTypes();

        if (!cancelled) {
          setTypes(data);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError("بارگذاری انواع دارو ناموفق بود.");
        }
      } finally {
        if (!cancelled) {
          setLoadingTypes(false);
        }
      }
    }

    loadTypes();

    return () => {
      cancelled = true;
    };
  }, []);

  // Load units whenever the selected medicine type changes.
  useEffect(() => {
    if (!drugType) {
      setUnits([]);
      setUnit("");
      return;
    }

    let cancelled = false;

    async function loadUnits() {
      try {
        setLoadingUnits(true);
        setUnits([]);
        setUnit("");

        const data = await getMedicineUnits(drugType);

        if (!cancelled) {
          setUnits(data);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setUnits([]);
          setError("بارگذاری واحدهای دارو ناموفق بود.");
        }
      } finally {
        if (!cancelled) {
          setLoadingUnits(false);
        }
      }
    }

    loadUnits();

    return () => {
      cancelled = true;
    };
  }, [drugType]);

  // Generate and clean up the local image preview URL.
  useEffect(() => {
    if (!image) {
      setPreview("");
      return;
    }

    const url = URL.createObjectURL(image);
    setPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [image]);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("لطفاً یک فایل تصویری انتخاب کنید.");
      event.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("حجم تصویر باید کمتر از ۵ مگابایت باشد.");
      event.target.value = "";
      return;
    }

    setError("");
    setImage(file);
  };

  const handleAdd = async (event) => {
    event.preventDefault();
    setError("");

    if (!drugType || !drugName.trim() || !drugDose.trim() || !unit) {
      setError("لطفاً نوع دارو، نام، دوز و واحد را انتخاب یا وارد کنید.");
      return;
    }

    if (!Number.isInteger(Number(qty)) || Number(qty) < 1) {
      setError("تعداد باید حداقل ۱ باشد.");
      return;
    }

    // Store IDs as well as labels to make future server submission easier.
    const selectedType = types.find(
      (item) => String(item.id) === String(drugType)
    );

    const selectedUnit = units.find(
      (item) => String(item.id) === String(unit)
    );

    if (!selectedType || !selectedUnit) {
      setError("نوع دارو یا واحد انتخاب‌شده معتبر نیست.");
      return;
    }

    try {
      setSaving(true);

      const imageBase64 = image ? await fileToBase64(image) : null;

      addItem({
        type: "otc",
        drugType: String(selectedType.id),
        drugTypeName: selectedType.name,
        drugName: drugName.trim(),
        drugDose: drugDose.trim(),
        qty: Number(qty),
        unit: String(selectedUnit.id),
        unitName: selectedUnit.name,
        image: imageBase64,
        fileName: image?.name || null,
        mimeType: image?.type || null,
        sameDrug,
      });

      navigate("/dashboard/pharmacy/prescription-cart");
    } catch (err) {
      console.error(err);
      setError("افزودن دارو به سبد خرید ناموفق بود.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="otc-prescription" dir="rtl">
      <header className="otc-header">
        <div className="otc-header-icon">
          <Pill size={28} />
        </div>

        <div>
          <h2>داروهای بدون نسخه</h2>
          <p>اطلاعات داروی موردنظر خود را وارد کنید.</p>
        </div>
      </header>

      <form onSubmit={handleAdd} noValidate>
        <div className="otc-fields-grid">
          <div className="otc-field">
            <label htmlFor="otc-drug-type">
              <Pill size={17} />
              نوع دارو <span>*</span>
            </label>

            <select
              id="otc-drug-type"
              value={drugType}
              disabled={loadingTypes}
              onChange={(event) => {
                setDrugType(event.target.value);
                setError("");
              }}
            >
              <option value="">
                {loadingTypes ? "در حال بارگذاری..." : "نوع دارو را انتخاب کنید"}
              </option>

              {types.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className="otc-field">
            <label htmlFor="otc-drug-name">
              <Package size={17} />
              نام دارو <span>*</span>
            </label>

            <input
              id="otc-drug-name"
              value={drugName}
              onChange={(e) => setDrugName(e.target.value)}
              placeholder="نام دارو را وارد کنید"
            />
          </div>

          <div className="otc-field">
            <label htmlFor="otc-drug-dose">
              <Hash size={17} />
              دوز / مقدار دارو <span>*</span>
            </label>

            <input
              id="otc-drug-dose"
              value={drugDose}
              onChange={(e) => setDrugDose(e.target.value)}
              placeholder="مثلاً ۵۰۰ میلی‌گرم"
            />
          </div>

          <div className="otc-field">
            <label htmlFor="otc-qty">
              تعداد <span>*</span>
            </label>

            <input
              id="otc-qty"
              type="number"
              min="1"
              step="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </div>

          <div className="otc-field otc-field-full">
            <label htmlFor="otc-unit">
              <Package size={17} />
              واحد دارو <span>*</span>
            </label>

            <select
              id="otc-unit"
              value={unit}
              disabled={!drugType || loadingUnits || units.length === 0}
              onChange={(e) => setUnit(e.target.value)}
            >
              <option value="">
                {!drugType
                  ? "ابتدا نوع دارو را انتخاب کنید"
                  : loadingUnits
                    ? "در حال بارگذاری واحدها..."
                    : units.length === 0
                      ? "واحدی برای این نوع دارو ثبت نشده است"
                      : "واحد را انتخاب کنید"}
              </option>

              {units.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="otc-image-section">
          <label className="otc-section-label">
            <ImagePlus size={18} />
            تصویر دارو (اختیاری)
          </label>

          <div className="otc-image-box">
            {preview ? (
              <div className="otc-image-preview">
                <img src={preview} alt="پیش‌نمایش تصویر دارو" />

                <button
                  type="button"
                  className="otc-remove-image"
                  onClick={() => {
                    setImage(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  aria-label="حذف تصویر"
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <>
                <ImagePlus size={36} />
                <p>تصویر بسته دارو را انتخاب کنید.</p>
                <small>JPG، PNG یا WEBP · حداکثر ۵ مگابایت</small>
              </>
            )}

            <input
              ref={fileInputRef}
              id="otc-image"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="otc-file-input"
              onChange={handleImageChange}
            />

            <button
              type="button"
              className="otc-image-button"
              onClick={() => fileInputRef.current?.click()}
            >
              <ImagePlus size={18} />
              {image ? "تغییر تصویر" : "انتخاب تصویر"}
            </button>
          </div>
        </div>

        <label className="otc-same-drug">
          <input
            type="checkbox"
            checked={sameDrug}
            onChange={(e) => setSameDrug(e.target.checked)}
          />

          <span className="otc-custom-checkbox">
            <CheckCircle2 size={17} />
          </span>

          <span>

            <small>
              ارسال محصول مشابه در صورت موجود نبودن
            </small>
          </span>
        </label>

        {error && (
          <p className="otc-error" role="alert">
            {error}
          </p>
        )}

        <div className="otc-actions">
          <button
            type="submit"
            className="otc-add-button"
            disabled={saving || loadingTypes || loadingUnits}
          >
            {saving ? (
              <LoaderCircle className="otc-spinner" size={20} />
            ) : (
              <ShoppingCart size={20} />
            )}

            {saving ? "در حال افزودن..." : "افزودن دارو به سبد خرید"}
          </button>
        </div>
      </form>

            <div className="prescription-upload-notice">
                 <div className="prescription-notice-heading">
          <span className="prescription-notice-info">i</span>
          <strong> راهنمای ثبت</strong>
        </div>
      
        <ul>
          <li>
            نام و مشخصات داروی بدون نسخه را به صورت موردی وارد کنید (در هر قسمت فقط یک مورد)

</li>
          <li> 
            داروهای بدون نسخه (OTC:Over-the-counter drug) داروهایی هستند که نیاز به نسخه پزشک نداشته و با نظارت دکتر داروساز عرضه می شوند.
 
</li>
        
        </ul>
      </div>

    </section>
  );
}
